import { Controller } from "@hotwired/stimulus";

/**
 * Manages the tabbed size picker (Aspect / Size / Resolution). The active tab
 * determines which canonical param the playground sends at generation time:
 *
 *   - aspect:     send `aspect_ratio`
 *   - size:       send `size` (W×H), no aspect_ratio
 *   - resolution: send `aspect_ratio` + `resolution` tier
 *
 * Persists the chosen tab per mode so reloads remember the user's preferred
 * mental model. Conversion between forms happens on the gateway, not here.
 */
export default class SizePickerController extends Controller {
  static targets = ["tab", "pane", "intentInput"];
  static values = {
    storageKey: String,
    defaultRatio: String,
  };

  declare tabTargets: HTMLButtonElement[];
  declare paneTargets: HTMLElement[];
  declare intentInputTarget: HTMLInputElement;
  declare storageKeyValue: string;
  declare hasStorageKeyValue: boolean;

  private static readonly DEFAULT_INTENT = "aspect";

  connect(): void {
    const stored = this.readStoredIntent();
    this.activate(stored || SizePickerController.DEFAULT_INTENT);
  }

  selectTab(event: Event): void {
    const button = event.currentTarget as HTMLButtonElement;
    const intent = button.dataset.intent;
    if (!intent) return;
    this.activate(intent);
    this.persistIntent(intent);
    // Recompute size payload now that the user shifted intent.
    this.intentInputTarget.dispatchEvent(new Event("change", { bubbles: true }));
  }

  selectSizePreset(event: Event): void {
    const button = event.currentTarget as HTMLButtonElement;
    const size = button.dataset.size;
    if (!size) return;
    const [w, h] = size.split("x");
    const wInput = this.element.querySelector<HTMLInputElement>('[data-playground-target="customSizeWidth"]');
    const hInput = this.element.querySelector<HTMLInputElement>('[data-playground-target="customSizeHeight"]');
    if (wInput) {
      wInput.value = w;
      wInput.dispatchEvent(new Event("input", { bubbles: true }));
    }
    if (hInput) {
      hInput.value = h;
      hInput.dispatchEvent(new Event("input", { bubbles: true }));
    }
  }

  private activate(intent: string): void {
    this.intentInputTarget.value = intent;
    this.tabTargets.forEach((tab) => {
      tab.dataset.active = tab.dataset.intent === intent ? "true" : "false";
    });
    this.paneTargets.forEach((pane) => {
      pane.hidden = pane.dataset.intent !== intent;
    });
  }

  private readStoredIntent(): string | null {
    if (!this.hasStorageKeyValue) return null;
    try {
      return localStorage.getItem(this.storageKeyValue);
    } catch {
      return null;
    }
  }

  private persistIntent(intent: string): void {
    if (!this.hasStorageKeyValue) return;
    try {
      localStorage.setItem(this.storageKeyValue, intent);
    } catch {
      /* localStorage unavailable */
    }
  }
}
